/**
 * Shared prominence classification for color mentions in a design brief.
 *
 * A brief often names several colors in one message — the dress's primary
 * color plus accent details ("nine subtle gemstones: red, blue, turquoise…").
 * Field extraction and grounding both need to know which mentions describe the
 * dress itself and which describe embellishment, so the logic lives here once
 * (the July session audit found a brief stating "ruby/crimson red as the
 * primary color" extracted as color "Turquoise" — an accent gemstone — and the
 * grounding check rubber-stamping it).
 */

// Longest color names first, so compound shades are matched as one modifier.
// Shared by prompt reconciliation and the scope of coordinated exclusions.
export const DESIGN_COLOR_PATTERN =
  /\b(?:(?:emerald|sage|mint)[-\s]+green|(?:ruby|crimson)[-\s]+red|(?:dusty|powder|sky|royal|midnight|navy)[-\s]+blue|dusty[-\s]+(?:rose|pink)|midnight[-\s]+navy|off[-\s]+white|(?:bright|pure|soft)[-\s]+white|emerald|burgundy|wine|oxblood|merlot|crimson|ruby|scarlet|navy|cobalt|sapphire|teal|turquoise|sage|olive|mint|forest[-\s]+green|lavender|lilac|mauve|plum|violet|purple|champagne|ivory|cream|blush|coral|peach|terracotta|rust|mustard|taupe|mocha|charcoal|beige|silver|gold|black(?![-\s]tie)|white|gray|grey|brown|green|yellow|orange|pink|blue|red)\b/gi;

/**
 * The mention's sentence describes embellishment/detailing rather than the
 * dress's own color: gemstones, beading, trim, sashes, embroidery, "pops of".
 */
export const ACCENT_COLOR_CONTEXT =
  /\b(?:accents?|accent(?:ed|ing)?|stones?|gem(?:stone)?s?|jewels?|bead(?:s|ing|ed|work)?|embellish\w*|sequins?|crystals?|rhinestones?|trims?|trimmed|sash(?:es)?|embroider\w*|appliqu\w*|piping|lining|touch(?:es)?\s+of|hints?\s+of|pops?\s+of|splash(?:es)?\s+of)\b/i;

/**
 * The mention is explicitly declared the dress's main color ("as the primary
 * color", "mostly ivory", "predominantly black").
 */
export const PRIMARY_COLOR_CONTEXT =
  /\b(?:primary|main|base|dominant|overall)\s+colou?r|\bmostly\b|\bpredominantly\b|\ball[-\s]over\b/i;

/**
 * The sentence containing `index` in `message`, capped at `cap` characters on
 * each side so a run-on brief can't drag unrelated clauses into the context.
 */
export function sentenceAround(
  message: string,
  index: number,
  matchLength = 0,
  cap = 90
): string {
  const boundary = /[.!?\n]/;
  let start = index;
  while (start > 0 && index - start < cap && !boundary.test(message[start - 1]))
    start--;
  let end = index + matchLength;
  while (
    end < message.length &&
    end - (index + matchLength) < cap &&
    !boundary.test(message[end])
  )
    end++;
  return message.slice(start, end);
}

export type ColorMentionProminence = "primary" | "regular" | "accent";

/** Classifies one color mention by its surrounding sentence. */
export function classifyColorMention(
  message: string,
  index: number,
  matchLength: number
): ColorMentionProminence {
  const sentence = sentenceAround(message, index, matchLength);
  if (PRIMARY_COLOR_CONTEXT.test(sentence)) return "primary";
  if (ACCENT_COLOR_CONTEXT.test(sentence)) return "accent";
  return "regular";
}
